/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { Object3D } from 'three';
/**
 * Helper class to store track-function and accessor
 */
class Track {
    constructor(attr, func, accessor) {
        this._defaultAccessor = (obj, val) => {
            // tslint:disable-next-line:no-any
            obj[this._attr] = val;
        };
        this._attr = attr;
        this._func = func;
        if (!!accessor) {
            this._accessor = accessor;
        }
        else {
            this._accessor = this._defaultAccessor;
        }
    }
    apply(obj, x) {
        const y = this._func(x);
        this._accessor(obj, y);
    }
}
/**
 *  CurtainRod : to move objects along a "curtain rod"
 */
export class CurtainRod extends Object3D {
    /**
     * constructor
     * @param objList list of Object3D (members of the curtain rod, e.g. planes showing images)
     * @param visibleObjects how many objects should be visible at once
     */
    constructor(objList, visibleObjects = 7) {
        super();
        this._tracks = new Array();
        this._visibleObjects = 7;
        this._offset = 0.5;
        this.setObjectList(objList);
        this._visibleObjects = visibleObjects;
        this._sFunc = (x) => x;
    }
    /**
     * this is a convenience function to create a coverflow
     * @param objList list of Object3D (members of the coverflow, e.g. planes showing images)
     */
    static createCoverFlow(objList) {
        const rod = new CurtainRod(objList);
        rod.addTrack('x', (x) => (x - 0.5) * 4.5, (obj, x) => obj.position.x = x);
        rod.addTrack('z', (z) => 1.6 * Math.exp(-30 * Math.pow(z - 0.5, 2.0)), (obj, x) => obj.position.z = x);
        rod.addTrack('alpha', (a) => (a - 0.5) * -Math.PI, (obj, x) => obj.rotation.y = x);
        // rod.addTrack('transparency', (a:number, )=>Math.exp(-20*Math.pow(a-0.5,2.0)), (obj:Object3D, x:number)=>((obj as Mesh).material as Material).opacity = x);
        return rod;
    }
    /**
     * sets the list of objects
     * @param objList list of Object3D (members of the curtain rod, e.g. planes showing images)
     */
    setObjectList(objList) {
        this._objList = objList;
    }
    /**
     * sets the s function
     * the s function is a mapping function f(0..1) => 0..1
     * this function can be used to implement e.g. dilation
     * the default is (x)=>x
     * @param func the s function
     */
    setSFunction(func) {
        this._sFunc = func;
    }
    /**
     * adds a 'Track' to the curtain rod
     * a Track is function that describes the curve of any attribute of an Object3D
     * @param attr the attribute to change
     * @param func the function [(0..1)=>number] that changes the attribute
     * @param accessor optional accessor to change an attribute
     */
    addTrack(attr, func, accessor) {
        this._tracks.push(new Track(attr, func, accessor));
    }
    /**
     * defines where in the track function the selected Object should be
     * @param offset
     */
    setOffset(offset) {
        this._offset = offset;
    }
    /**
     * sets the position between the objects
     * e.g. 0: shows the first object
     * 1: shows the second object
     * 0.5 is between the first and second object
     * 1.5 is between the second and the third object
     * @param t is the value from 0 to number of objects-1
     */
    setFractionalIndex(t) {
        if (!!this._objList && this._objList.length >= 1) {
            this._objList.forEach((e, i) => {
                e.visible = Math.abs(i - t) < (this._visibleObjects / 2.0);
                if (e.visible) {
                    this._setupSingleObject(i - t, e);
                }
            });
        }
    }
    /**
     * property setter
     */
    set index(t) {
        this.setFractionalIndex(t);
    }
    _setupSingleObject(relI, obj) {
        const x = relI / (this._visibleObjects - 1.0);
        const ys = this._sFunc(x + this._offset);
        this._tracks.forEach((track) => {
            track.apply(obj, ys);
        });
    }
}
//# sourceMappingURL=CurtainRod.js.map